import { useQuery, useInfiniteQuery, useMutation, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { ApiRequestError, getApiErrorMessage } from '../lib/apiErrors';
import { showToast } from '../lib/toast';
import type { PortalCollectionsResponse } from '../types/articleCollections';
import type { 
  CustomerPortalArticleDto, 
  CustomerPortalArticleDetailDto,
  CustomerPortalArticleListResponse,
} from '../types/articles';

// Customer Portal Knowledge Base specific query keys
const knowledgeBaseQueryKeys = {
  articles: {
    all: ['customer-portal', 'knowledge-base', 'articles'] as const,
    lists: () => [...knowledgeBaseQueryKeys.articles.all, 'list'] as const,
    list: (params: Record<string, unknown>) => [...knowledgeBaseQueryKeys.articles.lists(), params] as const,
    details: () => [...knowledgeBaseQueryKeys.articles.all, 'detail'] as const,
    detail: (id: string) => [...knowledgeBaseQueryKeys.articles.details(), id] as const,
    popular: (count: number) => [...knowledgeBaseQueryKeys.articles.all, 'popular', count] as const,
    featured: () => [...knowledgeBaseQueryKeys.articles.all, 'featured'] as const,
    search: (query: string, params: Record<string, unknown>) => [...knowledgeBaseQueryKeys.articles.all, 'search', query, params] as const,
    suggest: (query: string) => [...knowledgeBaseQueryKeys.articles.all, 'suggest', query] as const,
    related: (text: string) => [...knowledgeBaseQueryKeys.articles.all, 'related', text] as const,
  },
  collections: {
    all: ['customer-portal', 'knowledge-base', 'collections'] as const,
  },
};

interface ArticleListParams {
  page?: number;
  pageSize?: number;
  // A collection includes its sections
  collectionId?: string;
  isFeatured?: boolean;
  searchQuery?: string;
  sortBy?: string;
  sortDescending?: boolean;
}


// Get customer portal articles
export function useCustomerPortalArticles(params: ArticleListParams = {}, enabled: boolean = true) {
  return useQuery<CustomerPortalArticleListResponse>({
    queryKey: knowledgeBaseQueryKeys.articles.list(params as Record<string, unknown>),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalArticles(params);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles'));
      }
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Portal article addresses use the slug; links from before slugs (and ticket links) use the id - both open the article
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The address segment for an article in the portal: its slug, or its id when it has none */
export function portalArticleParam(article: { id: string; slug?: string | null }): string {
  return article.slug || article.id;
}

// The portal's "Other articles": published articles filed in no collection (?collection=uncategorized)
export const UNCATEGORIZED_COLLECTION = 'uncategorized';

// Get single customer portal article, by slug or id
export function useCustomerPortalArticle(articleIdOrSlug: string) {
  const queryClient = useQueryClient();

  return useQuery<CustomerPortalArticleDetailDto>({
    queryKey: knowledgeBaseQueryKeys.articles.detail(articleIdOrSlug),
    queryFn: async () => {
      const response = GUID.test(articleIdOrSlug)
        ? await apiService.getCustomerPortalArticle(articleIdOrSlug)
        : await apiService.getCustomerPortalArticleBySlug(articleIdOrSlug);
      if (!response.success || !response.data) {
        // Keeps the status: 404 means the article was unpublished, archived or deleted - the page says so
        throw new ApiRequestError(getApiErrorMessage(response, 'Failed to fetch article'), response.statusCode);
      }
      
      // Opened by id, the page moves to the slug address: have the article there already, so it isn't fetched (and
      // flashes) again
      if (response.data.slug && response.data.slug !== articleIdOrSlug) {
        queryClient.setQueryData(knowledgeBaseQueryKeys.articles.detail(response.data.slug), response.data);
      }

      // Invalidate article lists and search results to show updated view counts
      // This ensures that when users go back to the knowledge base, they see the updated view count
      queryClient.invalidateQueries({ 
        queryKey: knowledgeBaseQueryKeys.articles.lists(),
        refetchType: 'none' // Don't immediately refetch, just mark as stale
      });
      queryClient.invalidateQueries({ 
        queryKey: knowledgeBaseQueryKeys.articles.all,
        refetchType: 'none'
      });

      return response.data;
    },
    enabled: !!articleIdOrSlug,
  });
}

/** Search-as-you-type: the best few matches for what's in the box (2 characters or more); not logged as a search */
export function useCustomerPortalSuggestions(query: string) {
  const trimmed = query.trim();
  return useQuery<CustomerPortalArticleDto[]>({
    queryKey: knowledgeBaseQueryKeys.articles.suggest(trimmed),
    queryFn: async () => {
      const response = await apiService.suggestCustomerPortalArticles(trimmed);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to suggest articles'));
      }
      return response.data;
    },
    enabled: trimmed.length >= 2,
    staleTime: 60 * 1000,
    // The last suggestions stay up while the next arrive, instead of the list flickering empty
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Articles related to what a customer is writing (the new-ticket form's subject) - 3 characters or more */
export function useCustomerPortalRelatedArticles(text: string) {
  const trimmed = text.trim();
  return useQuery<CustomerPortalArticleDto[]>({
    queryKey: knowledgeBaseQueryKeys.articles.related(trimmed),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalRelatedArticles(trimmed);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to find related articles'));
      }
      return response.data;
    },
    enabled: trimmed.length >= 3,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    // Only a helping hand: a failure (a private knowledge base before sign-in) just shows nothing
    retry: false,
  });
}

/**
 * Counts that a visitor opened a result of their search, for the staff search insights. Fire and forget - it must
 * never get in the way of opening the article.
 */
export function recordPortalSearchClick(searchId: string | null | undefined, articleId: string) {
  if (!searchId) return;
  void apiService.recordCustomerPortalSearchClick(searchId, articleId).catch(() => undefined);
}

// The knowledge base's collections and sections that hold published articles, with counts
export function useCustomerPortalCollections() {
  return useQuery<PortalCollectionsResponse>({
    queryKey: knowledgeBaseQueryKeys.collections.all,
    queryFn: async () => {
      const response = await apiService.getCustomerPortalCollections();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch collections'));
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Get popular articles
export function useCustomerPortalPopularArticles(count: number = 10) {
  return useQuery<CustomerPortalArticleDto[]>({
    queryKey: knowledgeBaseQueryKeys.articles.popular(count),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalPopularArticles(count);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch popular articles'));
      }
      return response.data;
    },
  });
}

// Get featured articles
export function useCustomerPortalFeaturedArticles() {
  return useQuery<CustomerPortalArticleDto[]>({
    queryKey: knowledgeBaseQueryKeys.articles.featured(),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalFeaturedArticles();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch featured articles'));
      }
      return response.data;
    },
  });
}

// A collection's articles (sections included) in its curated order - one page, for a short list (related articles)
export function useCustomerPortalArticlesByCollection(collectionId?: string, enabled: boolean = true, pageSize: number = 50) {
  return useCustomerPortalArticles(
    { collectionId, pageSize, sortBy: 'sortOrder', sortDescending: false },
    enabled && !!collectionId
  );
}

const PORTAL_PAGE_SIZE = 20;

/**
 * A collection's page: its articles (sections included) in curated order, a page at a time - "Show more" loads the
 * next. Every article is reachable however large the collection is.
 */
export function useCustomerPortalCollectionArticles(collectionId: string | undefined) {
  const uncategorized = collectionId === UNCATEGORIZED_COLLECTION;
  return useInfiniteQuery({
    queryKey: [...knowledgeBaseQueryKeys.articles.lists(), 'collection', collectionId, PORTAL_PAGE_SIZE],
    queryFn: async ({ pageParam }) => {
      const response = await apiService.getCustomerPortalArticles({
        ...(uncategorized ? { uncategorized: true } : { collectionId }),
        page: pageParam,
        pageSize: PORTAL_PAGE_SIZE,
        sortBy: 'sortOrder',
        sortDescending: false,
      });
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch articles'));
      }
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (last) => (last.currentPage < last.totalPages ? last.currentPage + 1 : undefined),
    enabled: !!collectionId,
  });
}

/** Search results, best match first, a page at a time ("Show more" loads the next) */
export function useCustomerPortalSearch(query: string, collectionId?: string) {
  const trimmed = query.trim();
  return useInfiniteQuery({
    queryKey: [...knowledgeBaseQueryKeys.articles.all, 'search-pages', trimmed, collectionId, PORTAL_PAGE_SIZE],
    queryFn: async ({ pageParam }) => {
      const response = await apiService.searchCustomerPortalArticles({
        query: trimmed,
        collectionId,
        page: pageParam,
        pageSize: PORTAL_PAGE_SIZE,
      });
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to search articles'));
      }
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (last) => (last.currentPage < last.totalPages ? last.currentPage + 1 : undefined),
    enabled: trimmed.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function useSubmitArticleFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, request }: { articleId: string; request: { isHelpful: boolean; comment?: string } }) => {
      const response = await apiService.submitArticleFeedback(articleId, request);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to submit feedback'));
      }
      return response.data;
    },
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({ queryKey: ['customer-portal', 'knowledge-base', 'feedback', articleId] });
      queryClient.invalidateQueries({ queryKey: ['articles', 'feedback', 'summary', articleId] });
      queryClient.invalidateQueries({ queryKey: ['articles', 'feedback', 'list', articleId] });
    },
    onError: (error: Error) => {
      // The feedback buttons fire and forget, so without this a failed submission looks like nothing happened
      showToast.error('Feedback Not Sent', error.message);
    },
  });
}

export function useMyArticleFeedback(articleId: string | undefined) {
  return useQuery({
    queryKey: ['customer-portal', 'knowledge-base', 'feedback', articleId],
    queryFn: async () => {
      const response = await apiService.getMyArticleFeedback(articleId!);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch feedback'));
      }
      return response.data;
    },
    enabled: !!articleId,
    staleTime: 300000,
  });
}